import { useMutation } from "@apollo/client/react";
import { Button, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { PlugsIcon } from "@phosphor-icons/react/dist/csr/Plugs";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { toast } from "react-toastify";
import { DOWNLOAD_FILES_QUERY, UPLOAD_FILES_QUERY } from "../../graphql";
import { DownloadsQuery, UploadsQuery, SaveDownloadDocument, SaveUploadDocument } from "../../graphql/types";
import { ActionMenuButton } from "../ActionsMenu/ActionMenuButton";

const DownloadAbandonButton = ({
  id,
  withText,
  onClick,
  disabled,
}: {
  id: string;
  withText?: boolean;
  onClick: () => void;
  disabled?: boolean;
  downloadStatus: string;
}) => {
  const [handleAbandonDownload, { loading, error }] = useMutation(SaveDownloadDocument, {
    variables: {
      id: id,
      status: "abandoned",
    },
    optimisticResponse: {
      __typename: "Mutation",
      save_download: {
        __typename: "SaveDownloadBundle",
        ok: true,
      },
    },
    update(cache) {
      const existingDownloads: DownloadsQuery | null = cache.readQuery({
        query: DOWNLOAD_FILES_QUERY,
      });

      const newDownloads = existingDownloads?.downloads?.edges.filter((download) => download.node.id !== id) || [];
      cache.writeQuery<DownloadsQuery>({
        query: DOWNLOAD_FILES_QUERY,
        data: {
          __typename: "Query",
          downloads:
            newDownloads.length && existingDownloads?.downloads
              ? {
                  ...existingDownloads.downloads,
                  edges: newDownloads,
                }
              : null,
        },
      });
    },
    refetchQueries: () => ["downloads"],
    awaitRefetchQueries: false,
    onCompleted: () => {
      toast.success("Download abandoned correctly.");
    },
  });

  // TODO Make this version the standard (#21394 or #21412)
  if (withText)
    return (
      <ActionMenuButton
        Icon={PlugsIcon}
        onClick={() => {
          onClick();
          handleAbandonDownload();
        }}
        text="Abandon"
        tooltipText="The download has already finished"
        disabled={disabled}
      />
    );

  if (loading) {
    return <CircularProgress size="1em" color="info" />;
  } else if (error) {
    return <WarningIcon color="error" />;
  }
  return (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        handleAbandonDownload();
      }}
    >
      <Tooltip id="tooltip" title="Abandon the current download." placement="left" arrow>
        <XIcon />
      </Tooltip>
    </IconButton>
  );
};

const UploadAbandonButton = ({ id, withText, onClick }: { id: string; withText?: boolean; onClick: () => void }) => {
  const theme = useTheme();

  const [handleAbandonUpload, { loading, error }] = useMutation(SaveUploadDocument, {
    variables: {
      id,
      status: "abandoned",
    },
    optimisticResponse: {
      __typename: "Mutation",
      save_upload: {
        __typename: "SaveUpload",
        ok: true,
      },
    },
    update(cache) {
      const existingUploads: UploadsQuery | null = cache.readQuery({
        query: UPLOAD_FILES_QUERY,
      });
      const newUploads = existingUploads?.uploads?.edges?.filter((upload) => upload.node.id !== id) || [];
      cache.writeQuery<UploadsQuery>({
        query: UPLOAD_FILES_QUERY,
        data: {
          __typename: "Query",
          uploads:
            newUploads.length && existingUploads?.uploads
              ? {
                  ...existingUploads.uploads,
                  edges: newUploads,
                }
              : null,
        },
      });
    },
    refetchQueries: () => ["uploads"],
    awaitRefetchQueries: false,
    onCompleted: () => {
      toast.success("Upload abandoned correctly.");
    },
  });

  // TODO Make this version the standard (#21394 or #21412)
  if (withText)
    return (
      <Button
        variant="text"
        sx={{ justifyContent: "flex-start", alignItems: "flex-start" }}
        onClick={() => {
          onClick();
          handleAbandonUpload();
        }}
        aria-label="Abandon"
      >
        <PlugsIcon size={24} color={theme.palette.text50.main} />
        <Typography variant="body.medium.regular" sx={{ color: "text40.main" }}>
          Abandon
        </Typography>
      </Button>
    );

  if (loading) {
    return <CircularProgress size="1em" color="info" />;
  } else if (error) {
    return <WarningIcon color="error" />;
  } else {
    return (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleAbandonUpload();
        }}
      >
        <Tooltip id="tooltip" title="Abandon the current upload.">
          <XIcon />
        </Tooltip>
      </IconButton>
    );
  }
};

export { DownloadAbandonButton, UploadAbandonButton };
